import React, { useEffect, useState, useRef, useCallback } from "react"
import { runBatchLoader } from "utils/batchLoader"
import MetaTags from "react-meta-tags"
import { Col, Container, Row, Card, CardBody } from "reactstrap"
//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"
import { deliveryModeOptions } from "constants/layout"
import { get } from "helpers/api_helper"
import { CHALLAN_URL } from "helpers/url_helper"
import "react-datepicker/dist/react-datepicker.css"
import { useSelector } from "react-redux"
import { customFormatDate } from "helpers/basic_helper"
import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import ClientDropdown from "utils/client_dropdown"
import "flatpickr/dist/themes/material_red.css"
import Flatpickr from "react-flatpickr"
import Timezone from "utils/timezone"
import moment from "moment"

const sort_date_filters = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 15 days", value: "last_15_days" },
  { label: "This month", value: "this_month" },
  { label: "Last month", value: "last_month" },
  { label: "Last 45 days", value: "last_45_days" },
  { label: "Last 60 days", value: "last_60_days" },
]

const isType = "challan"
const Challans = () => {
  const { loading } = useSelector(state => state.Challans)
  const [challans, setChallans] = useState([])
  const [challansLoad, setChallansLoad] = useState(false)
  const [selectedRangeValue, setSelectedRangeValue] = useState(null)
  const [batchProgress, setBatchProgress] = useState(null)
  const batchCancelRef = useRef(false)
  const isBatchRunningRef = useRef(false)
  const totalRowsRef = useRef(0)

  const [filters, setFilters] = useState({
    search: null,
    client: null,
    _startdate: null,
    _enddate: null,
  })

  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })

  useEffect(() => {
    if (challans?.total_rows) {
      totalRowsRef.current = challans.total_rows
    }
  }, [challans])

  const handleCancelBatch = () => {
    batchCancelRef.current = true
    isBatchRunningRef.current = false
    setBatchProgress(prev => prev ? ({ ...prev, active: false, stopped: true }) : null)
    setChallansLoad(false)
  }

  const handlePaginationChange = useCallback((newPag) => {
    setPagination(prev => {
      if (
        prev.page === newPag.page &&
        prev.limit === newPag.limit &&
        prev.search === newPag.search &&
        Boolean(prev.isAll) === Boolean(newPag.isAll)
      ) {
        return prev
      }
      return { ...prev, ...newPag }
    })
  }, [])

  const getData = async () => {
    const isAllSelected = pagination?.isAll || pagination?.limit === 'all' || (typeof pagination?.limit === 'number' && pagination?.limit > 100)

    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    )

    if (isAllSelected) {
      batchCancelRef.current = true
      await new Promise(r => setTimeout(r, 60))
      batchCancelRef.current = false
      isBatchRunningRef.current = true

      setChallansLoad(true)
      try {
        await runBatchLoader({
          fetchPage: async (page, batchSize) => {
            const queryParams = new URLSearchParams({
              ...cleanFilters,
              page,
              limit: batchSize,
              search: pagination?.search || '',
            }).toString()
            const res = await get(`${CHALLAN_URL}?${queryParams}`)
            return res?.response || res || {}
          },
          batchSize: 250,
          initialTotal: totalRowsRef.current || 0,
          onProgress: setBatchProgress,
          onRowsLoaded: (accumulatedRows, totalCount) => {
            setChallans({
              total_rows: totalCount,
              rows: accumulatedRows,
            })
          },
          cancelRef: batchCancelRef,
          runningRef: isBatchRunningRef,
        })
      } catch (err) {
        console.error("Error batch loading challans:", err)
      } finally {
        setChallansLoad(false)
      }
    } else {
      setChallansLoad(true)
      try {
        const queryParams = new URLSearchParams(cleanFilters).toString()
        const res = await get(
          `${CHALLAN_URL}?${queryParams}&page=${pagination?.page}&limit=${pagination?.limit}&search=${pagination?.search}`
        )
        if (res && res.status == 200 && res.response) {
          setChallans(res.response)
          setBatchProgress(null)
        }
      } catch (err) {
        console.error("Error loading challans:", err)
      } finally {
        setChallansLoad(false)
      }
    }
  }

  useEffect(() => {
    getData()
  }, [pagination])

  const handleFilterChange = value => {
    let startDate = moment().endOf("day")
    let endDate = moment().endOf("day")

    switch (value) {
      case "today":
        startDate = moment().startOf("day")
        break
      case "yesterday":
        startDate = moment().subtract(1, "days").startOf("day")
        endDate = moment().subtract(1, "days").endOf("day")
        break
      case "last_7_days":
        startDate = moment().subtract(7, "days").startOf("day")
        break
      case "last_15_days":
        startDate = moment().subtract(15, "days").startOf("day")
        break
      case "this_month":
        startDate = moment().startOf("month")
        break
      case "last_month":
        startDate = moment().subtract(1, "months").startOf("month")
        endDate = moment().subtract(1, "months").endOf("month")
        break
      case "last_45_days":
        startDate = moment().subtract(45, "days").startOf("day")
        break
      case "last_60_days":
        startDate = moment().subtract(60, "days").startOf("day")
        break
    }

    setFilters(prev => ({
      ...prev,
      bydate: [startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD")],
    }))
  }

  /**
   * DataTable with Condition based row formating
   */
  let datatableData = [
    {
      dataField: "id",
      text: "Id",
    },
    {
      dataField: "challan_number",
      text: "Challan Number",
      formatter: (cell, row) => {
        return (
          <>
            <a
              title="Click to view challan"
              href={`/challan/print?_challan=${row.challan_number}`}
              target="_blank"
              rel="noreferrer"
            >
              # {row.challan_number}
            </a>
            <br />
            <span
              className={`badge badge-soft-${row.is_dispath == 1 ? "success" : "danger"
                } font-size-11`}
            >
              {row.is_dispath == 1 ? "Dispatched" : "Pending"}
            </span>
            <span className={`ms-2 badge badge-soft-warning font-size-11`}>
              {
                deliveryModeOptions?.filter(
                  v => v.value == row.delivery_mode
                )[0]?.label
              }
            </span>
          </>
        )
      },
    },
    {
      dataField: "challan_date",
      text: "Challan Date",
      formatter: (cell, row) =>
        customFormatDate(row?.challan_date, "DD MMM, YY"),
    },
    {
      dataField: "clientname",
      text: "#Client",
    },
    {
      dataField: "order_number",
      text: "Order Number",
    },
    {
      dataField: "order_date",
      text: "Order Date",
      formatter: (cell, row) => customFormatDate(row?.order_date, "DD MMM, YY"),
    },
    {
      dataField: "due_date",
      text: "Due Date",
      formatter: (cell, row) => customFormatDate(row?.due_date, "DD MMM, YY"),
    },
  ]

  const printPdf = challanNumber => {
    const CN = challanNumber.challan_number
    window.open(`/challan/print?_challan=${CN}`, "_blank")
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          {" "}
          <title>{isType} | Oralstop</title>{" "}
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Masters" breadcrumbItem={isType} />

          <Row>
            <Col xl="12">
              <Card className="cardfilters">
                <CardBody className="p-0 border-0 shadow-none">
                  <Row>
                    <Col xl="3">
                      <label>Search by Challan or Order Number</label>
                      <input
                        type="number"
                        className="form-control"
                        onChange={e =>
                          setFilters(prev => ({
                            ...prev,
                            search: e.target.value,
                          }))
                        }
                        placeholder="Please Search using Challan or Order Number"
                      />
                    </Col>
                    <Col xl="2">
                      <ClientDropdown
                        callback={e =>
                          setFilters(prev => ({ ...prev, client: e }))
                        }
                      />
                    </Col>
                    <Col xl={2}>
                      <label>Day wise Filter</label>
                      <Select
                        onChange={e => {
                          setSelectedRangeValue(e)
                          handleFilterChange(e.value)
                        }}
                        isClearable={true}
                        value={selectedRangeValue || ""}
                        options={sort_date_filters || []}
                        name="clients"
                        required={false}
                        placeholder={"Select Filter"}
                      />
                    </Col>
                    <Col xl={3}>
                      <label>Date Range</label>
                      <Flatpickr
                        className="form-control d-block"
                        placeholder="DD-MM-YYYY"
                        options={{
                          mode: "range",
                          dateFormat: "d-m-Y",
                        }}
                        onChange={e => {
                          const dates = e.map(dateString =>
                            Timezone(dateString)
                          )
                          setFilters(prev => ({
                            ...prev,
                            _startdate: dates[0],
                            _enddate: dates[1],
                          }))
                        }}
                      />
                    </Col>
                    <Col className="text-end mt-3">
                      <button
                        className="btn btn-primary mt-2"
                        style={{ width: "126px" }}
                        onClick={() => { }}
                      >
                        Filter
                      </button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  <Datatables
                    isSearch={true}
                    columns={datatableData}
                    showTableOnly={true}
                    rowsLength={challans?.total_rows || 0}
                    rows={challans?.rows || []}
                    keyField={"id"}
                    handleAddButton={() => {
                      setEditableObj(null)
                      isOpen(true)
                    }}
                    title="All Challans"
                    loading={challansLoad && (!batchProgress || batchProgress.loaded === 0)}
                    batchProgress={batchProgress}
                    onCancelBatch={handleCancelBatch}
                    isAdd={true}
                    isTableHead={true}
                    ssr={handlePaginationChange}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Challans
